/*-
 * *****
 * Concord
 * -----
 * Copyright (C) 2017 - 2018 Walmart Inc.
 * -----
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =====
 */

import * as React from 'react';
import { useCallback, useRef, useState } from 'react';
import { Breadcrumb, Icon, Menu } from 'semantic-ui-react';

import { LoadingState } from '../../../App';
import { Link } from 'react-router';
import { Navigate, Route, Routes } from 'react-router';
import { RouteComponentProps, withRouter } from '@/router';
import { NotFoundPage } from '../index';
import NodeRosterHostsList from './NodeRosterHostsList';
import NodeRosterArtifactsList from './NodeRosterArtifactsList';
import { BreadcrumbsToolbar } from '../../organisms';

type TabLink = 'hosts' | 'artifacts' | null;

const pathToTab = (s: string): TabLink => {
    if (s.endsWith('/hosts')) {
        return 'hosts';
    } else if (s.endsWith('/artifacts')) {
        return 'artifacts';
    }

    return null;
};

const NodeRosterPage = (props: RouteComponentProps) => {
    const activeTab = pathToTab(props.location.pathname);

    const stickyRef = useRef(null);

    const loading = React.useContext(LoadingState);
    const [refresh, toggleRefresh] = useState<boolean>(false);

    const refreshHandler = useCallback(() => {
        toggleRefresh((prevState) => !prevState);
    }, []);

    const baseUrl = `/noderoster`;

    return (
        <div ref={stickyRef}>
            <BreadcrumbsToolbar loading={loading} refreshHandler={refreshHandler}>
                <Breadcrumb.Section active={true}>Node Roster</Breadcrumb.Section>
            </BreadcrumbsToolbar>

            <Menu tabular={true} style={{ marginTop: 0 }}>
                <Menu.Item active={activeTab === 'hosts'}>
                    <Icon name="server" />
                    <Link to={`${baseUrl}/hosts`}>Hosts</Link>
                </Menu.Item>
                <Menu.Item active={activeTab === 'artifacts'}>
                    <Icon name="cubes" />
                    <Link to={`${baseUrl}/artifacts`}>Artifacts</Link>
                </Menu.Item>
            </Menu>

            <Routes>
                <Route index={true} element={<Navigate to="hosts" replace={true} />} />
                <Route path="hosts" element={<NodeRosterHostsList forceRefresh={refresh} />} />
                <Route
                    path="artifacts"
                    element={<NodeRosterArtifactsList forceRefresh={refresh} />}
                />
                <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </div>
    );
};

export default withRouter(NodeRosterPage);
